import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  PrimaryButton,
  ResponsiveImage, SecondaryButton,
} from '../../../components';
import { ReactComponent as HeartOutlineIcon } from '../../../assets/icons/heart/outline.svg';
import { ReactComponent as HeartSolidIcon } from '../../../assets/icons/heart/solid.svg';
import { ReactComponent as ChevronRightIcon } from '../../../assets/icons/chevron-right.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../assets/icons/chevron-left.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import useFavoriteToggle from '../../../hooks/useFavoriteToggle';
import useDragScroll from '../../../hooks/useDragScroll';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.listing} props.listing - The listing object
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const intl = useIntl();
  const { rootClassName, className, listing, images, imageVariants, thumbnailVariants } = props;

  // Use the favorite toggle hook
  const listingId = listing?.id?.uuid;
  const { onFavoriteClick, isFavorited, isToggling } = useFavoriteToggle(listingId);

  // Enable drag-to-scroll on thumbnails
  const galleryRef = useDragScroll('.image-gallery-thumbnails');

  // Auto-scroll thumbnail into view when slide changes
  useEffect(() => {
    if (!galleryRef.current) return;

    const thumbnailsContainer = galleryRef.current.querySelector('.image-gallery-thumbnails');
    const activeThumbnail = galleryRef.current.querySelector('.image-gallery-thumbnail.active');

    if (thumbnailsContainer && activeThumbnail) {
      const containerRect = thumbnailsContainer.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();

      // Check if thumbnail is outside the visible area
      const isOutOfView =
        thumbnailRect.left < containerRect.left ||
        thumbnailRect.right > containerRect.right;

      if (isOutOfView) {
        // Calculate the scroll position to center the thumbnail
        const scrollOffset = activeThumbnail.offsetLeft - (thumbnailsContainer.clientWidth / 2) + (activeThumbnail.clientWidth / 2);

        thumbnailsContainer.scrollTo({
          left: scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  }, [currentIndex, galleryRef]);

  const handleSlide = (index) => {
    setCurrentIndex(index);
  };

  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        paddingVariant="listing"
      >
        <div className={css.itemCentering}>
          {!isFullscreen && (
            <button
              type="button"
              className={classNames(css.favoriteButton, { [css.isToggling]: isToggling })}
              onClick={onFavoriteClick}
              disabled={isToggling}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              aria-busy={isToggling}
            >
              {isFavorited ? (
                <HeartSolidIcon className={css.heartIcon} />
              ) : (
                <HeartOutlineIcon className={css.heartIcon} />
              )}
            </button>
          )}
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <ChevronLeftIcon width={24} height={24} />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <ChevronRightIcon width={24} height={24} />
        </div>
      </button>
    );
  };

  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <PrimaryButton
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <CloseIcon width={24} height={24} />
      </PrimaryButton>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div ref={galleryRef}>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onScreenChange={onScreenChange}
        onSlide={handleSlide}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        renderFullscreenButton={renderFullscreenButton}
        {...IMAGE_GALLERY_OPTIONS}
      />
    </div>
  );
};

export default ListingImageGallery;
